'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { getFamilyRepository } from '@/contexts/family/server';

/**
 * Generate a TTS preview for a custom (cloned) voice that doesn't have one yet.
 * Enqueues to Python worker — result arrives via SSE.
 */
export async function generateCustomVoicePreview(
  voiceId: string,
): Promise<ActionResult<{ jobId: string }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };

    const repo = getFamilyRepository();
    const voices = await repo.getCustomVoices(familyId);
    const voiceEntry = voices.find((v) => v.voiceId === voiceId);

    if (!voiceEntry) {
      return { success: false, error: 'Voice not found' };
    }

    // If preview already exists, return as-is
    if (voiceEntry.previewUrl) {
      return { success: true, data: { jobId: 'existing' } };
    }

    const PREVIEW_TEXTS: Record<string, string> = {
      ru: 'Привет! Вот так звучит мой голос.',
      en: 'Hello! This is how my voice sounds.',
      cs: 'Ahoj! Takto zní můj hlas.',
      uk: 'Привіт! Ось як звучить мій голос.',
    };
    const text = PREVIEW_TEXTS[voiceEntry.language ?? 'ru'] ?? PREVIEW_TEXTS.ru;
    const blobPath = `voice-samples/${familyId}/${voiceEntry.id}/preview.mp3`;

    const { getAudioGenerationQueue } = await import(
      '@/contexts/ai/infrastructure/audioGenerationQueue'
    );

    const jobId = await getAudioGenerationQueue().enqueue({
      type: 'voice-preview',
      familyId,
      text,
      voiceId: voiceEntry.voiceId,
      blobPath,
    });

    return { success: true, data: { jobId } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generateCustomVoicePreview', e) };
  }
}
